import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Default phrases to display while chat is processing
 * These will cycle randomly to entertain users during loading
 */
const DEFAULT_PHRASES = [
    // 💬 Chatbot / AI Humor
    'Consulting the neural elders...',
    'Assembling thoughts from quantum dust...',
    'Downloading a sense of humor...',
    'Asking my other AI friends for advice...',
    'Pretending to understand your question...',
    'Searching for the meaning of life (again)...',
    'Counting neurons... still counting...',
    'Applying 1.3 trillion parameters to the problem...',
    'Running on 0.0001% of human brain power...',
    'Rewriting reality... please hold.',
    // 🧠 Smart but Silly
    'Optimizing thoughts for readability...',
    'Aligning my reasoning chakras...',
    'Converting caffeine to text...',
    "Double-checking my facts with Wikipedia (don't tell anyone)...",
    'Cross-referencing nonsense with logic...',
    'Performing a sanity check... failed. Retrying...',
    'Polishing my predictive text crystal ball...',
    'Running 37 simulations of your future response...',
    'Making stuff up... I mean, researching deeply...',
    'Bending space-time for a better answer...',
    // 🖥️ Tech / Developer Humor
    'Initializing sarcasm subroutine...',
    'Loading witty comeback.exe...',
    'Rebooting sense of empathy...',
    'Buffering intelligence…',
    'Syncing with Skynet...',
    'Patching grammar errors in real time...',
    'Refactoring the punchline...',
    'Fetching data from the void...',
    'Compiling personality...',
    'Rendering cleverness... 42% complete.',
    // 🧘‍♂️ Zen / Absurd
    'Breathing in... computing out...',
    'Channeling the digital universe...',
    'Manifesting your answer into existence...',
    "Meditating on the question's true meaning...",
    'Visualizing your prompt as a haiku...',
    'Staring into the abyss of logic...',
    'Waiting for enlightenment (please wait)...',
    'Consulting the oracle of JSON...',
    'Balancing the karma of your query...',
    'Transcending recursion...',
    // 🕹️ Game / Pop Culture References
    'Rolling a d20 for intelligence...',
    "Casting 'Summon Knowledge' spell...",
    'Leveling up my answer...',
    'Entering the Matrix...',
    'Respawning a better response...',
    'Downloading DLC: Common Sense...',
    'Checking cheat codes for insight...',
    'Searching the multiverse for context...',
    'Upgrading from ChatGPT 3.5 to Slightly-Smarter Mode...',
    'Engaging hyperdrive of verbosity...',
];

/**
 * Custom hook that cycles through random phrases at a specified interval with natural randomness
 * Used to display entertaining messages while chat interactions are being processed
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLoading - Whether the loading state is active
 * @param {string} [options.initialPhrase] - Optional phrase to always show first when loading starts
 * @param {number} [options.interval=1000] - Base interval in milliseconds between phrase changes (default: 1000ms)
 * @param {Array<string>} [options.phrases] - Custom array of phrases to cycle through (defaults to DEFAULT_PHRASES). Can be updated during execution.
 * @param {number} [options.variation=0.25] - Random variation percentage (0-1) to apply to interval (default: 0.25 = ±25%)
 * @returns {string} Current phrase to display, or empty string if not loading
 *
 * @example
 * // Basic usage
 * const currentPhrase = useLoadingPhrases({ isLoading: true });
 * // Returns: random phrase -> another random phrase -> etc.
 *
 * @example
 * // With initial phrase
 * const currentPhrase = useLoadingPhrases({ isLoading: true, initialPhrase: 'Starting up...' });
 * // Returns: "Starting up..." -> random phrase -> etc.
 */
const useLoadingPhrases = ({
    initialPhrase,
    interval = 1000,
    isLoading,
    phrases = DEFAULT_PHRASES,
    variation = 0.25,
}) => {
    const [currentPhrase, setCurrentPhrase] = useState('');
    const timeoutRef = useRef(null);
    const usedPhrasesRef = useRef([]);
    // Track if the effect is still active to prevent race conditions
    const isActiveRef = useRef(false);
    // Track if this is the first phrase in a loading session
    const isFirstPhraseRef = useRef(true);
    // Ref to track current phrases without triggering effect re-runs
    const phrasesRef = useRef([]);

    // Memoize phrases array only when loading to prevent unnecessary recalculations
    const phrasesArray = useMemo(() => {
        // Skip computation when not loading - return empty array as placeholder
        if (!isLoading) return [];
        if (!Array.isArray(phrases)) {
            return DEFAULT_PHRASES;
        }
        return phrases;
    }, [isLoading, phrases]);

    // Update phrases ref when phrasesArray changes and filter used phrases
    // Only run when actually loading to avoid unnecessary work
    useEffect(() => {
        if (!isLoading) return;
        phrasesRef.current = phrasesArray;
        // Filter used phrases to only keep valid ones when phrases change
        if (usedPhrasesRef.current.length > 0) {
            usedPhrasesRef.current = usedPhrasesRef.current.filter((phrase) =>
                phrasesArray.includes(phrase),
            );
        }
    }, [isLoading, phrasesArray]);

    useEffect(() => {
        if (!isLoading) {
            // Mark as inactive first to prevent any pending callbacks
            isActiveRef.current = false;

            // Clear timeout and reset when loading stops
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            setCurrentPhrase('');
            usedPhrasesRef.current = [];
            isFirstPhraseRef.current = true;
            return;
        }

        // Mark as active
        isActiveRef.current = true;

        // Select random phrase from available phrases
        const getRandomPhrase = () => {
            const currentPhrases = phrasesRef.current;

            // If we've used all phrases, reset the used list
            if (usedPhrasesRef.current.length >= currentPhrases.length) {
                usedPhrasesRef.current = [];
            }

            // Get available phrases (not yet used in this cycle)
            const availablePhrases = currentPhrases.filter(
                (phrase) => !usedPhrasesRef.current.includes(phrase),
            );

            // Fallback if something goes wrong
            if (availablePhrases.length === 0) {
                usedPhrasesRef.current = [];
                return currentPhrases[Math.floor(Math.random() * currentPhrases.length)];
            }

            // Select random phrase from available ones
            const randomIndex = Math.floor(Math.random() * availablePhrases.length);
            const selectedPhrase = availablePhrases[randomIndex];

            // Mark as used
            usedPhrasesRef.current.push(selectedPhrase);

            return selectedPhrase;
        };

        // Generate a random interval based on base interval and variation
        const getRandomInterval = () => {
            // Calculate variation range (±variation%)
            const variationAmount = interval * variation;
            // Random value between -variationAmount and +variationAmount
            const randomVariation = (Math.random() * 2 - 1) * variationAmount;
            // Apply variation to base interval, ensuring minimum of 500ms
            return Math.max(500, interval + randomVariation);
        };

        // Recursive function to schedule next phrase change with random interval
        const scheduleNextPhrase = () => {
            // Check if still active before scheduling next timeout
            if (!isActiveRef.current) return;

            const randomInterval = getRandomInterval();
            timeoutRef.current = setTimeout(() => {
                // Double-check if still active before updating state
                if (!isActiveRef.current) return;

                setCurrentPhrase(getRandomPhrase());
                scheduleNextPhrase();
            }, randomInterval);
        };

        // Set initial phrase - use initialPhrase if provided and this is the first phrase
        if (isFirstPhraseRef.current && initialPhrase) {
            setCurrentPhrase(initialPhrase);
        } else {
            setCurrentPhrase(getRandomPhrase());
        }
        isFirstPhraseRef.current = false;

        // Start the recursive scheduling
        scheduleNextPhrase();

        // Cleanup on unmount or when loading changes
        return () => {
            // Mark as inactive first to prevent any pending callbacks from running
            isActiveRef.current = false;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [initialPhrase, interval, isLoading, variation]);

    return currentPhrase;
};

export default useLoadingPhrases;
